import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, MapPin, Clock, Camera, CheckCircle2, Briefcase } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { WorkerJobDialog } from "@/components/worker/MyJobsTab";
import { api } from "@/lib/api";
import { fmtDuration, fmtDate, jobTypeLabel } from "@/lib/format";

const THREE_DAYS_MS = 3 * 24 * 60 * 60 * 1000;

// Worker's completed jobs — only those completed within the last 3 days stay visible.
export const CompletedJobsTab = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const all = await api.completedJobs();
      const cutoff = Date.now() - THREE_DAYS_MS;
      const recent = (all || []).filter((j) => j.completed_at && new Date(j.completed_at).getTime() >= cutoff);
      recent.sort((a, b) => new Date(b.completed_at) - new Date(a.completed_at));
      setJobs(recent);
    } catch { toast.error("Failed to load completed jobs"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>;

  return (
    <div className="fade-up">
      <h2 className="font-display text-2xl font-bold mb-1">Completed</h2>
      <p className="text-sm text-muted-foreground mb-6">Jobs you finished in the last 3 days.</p>
      {jobs.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-border rounded-2xl"><p className="text-muted-foreground">Nothing completed in the last 3 days.</p></div>
      ) : (
        <div className="bg-card border border-border rounded-2xl overflow-x-auto" data-testid="completed-jobs-list">
          <Table className="min-w-[720px]">
            <TableHeader>
              <TableRow>
                <TableHead>Job</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Address</TableHead>
                <TableHead>Completed</TableHead>
                <TableHead className="text-right">Time</TableHead>
                <TableHead className="text-right">Photos</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {jobs.map((j) => (
                <TableRow
                  key={j.id}
                  data-testid={`completed-card-${j.id}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelected(j)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelected(j); } }}
                  className="cursor-pointer hover:bg-secondary/50"
                >
                  <TableCell className="font-medium">
                    {j.name}
                    <div className="text-xs font-normal text-muted-foreground">{jobTypeLabel(j)}</div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    <span className="inline-flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5 shrink-0" /> {j.client || "—"}</span>
                  </TableCell>
                  <TableCell className="text-muted-foreground max-w-[220px]">
                    <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 shrink-0" /> <span className="line-clamp-1">{j.address || "—"}</span></span>
                  </TableCell>
                  <TableCell className="text-muted-foreground whitespace-nowrap text-sm">{j.completed_at ? fmtDate(j.completed_at) : "—"}</TableCell>
                  <TableCell className="text-right font-mono text-sm"><span className="inline-flex items-center gap-1 justify-end"><Clock className="w-3.5 h-3.5" /> {fmtDuration(j.total_seconds)}</span></TableCell>
                  <TableCell className="text-right font-mono text-sm"><span className="inline-flex items-center gap-1 justify-end"><Camera className="w-3.5 h-3.5" /> {j.photo_count}</span></TableCell>
                  <TableCell><span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-medium"><CheckCircle2 className="w-3.5 h-3.5" /> Done</span></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {selected && <WorkerJobDialog job={selected} onClose={() => setSelected(null)} onChanged={load} />}
    </div>
  );
};
